import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, MapPin, Layers, Crown } from 'lucide-react';
import type { EmployerLocation, EmployerDepartment } from './EmployerOrgStructuur';
import { EMPLOYER_ROLE_PRESETS } from '../config/employerPermissions';

export const TOEGANGSNIVEAUS = ['global_admin', 'vestigingsmanager', 'afdelingschef'] as const;
export type Toegangsniveau = (typeof TOEGANGSNIVEAUS)[number];

export interface TeamMember {
  employer_user_id: number;
  user_id: string;
  team_role: string;
  toegangsniveau: Toegangsniveau;
  /** Mag de huidige beheerder dit lid wijzigen (anti-sabotage, berekend door de backend) */
  bewerkbaar: boolean;
  invited_at: string;
  accepted_at: string | null;
  is_active: boolean;
  email: string;
  first_name: string;
  last_name: string;
  functietitel: string | null;
  permissions?: string[];
  location_id: string | null;
  department_id: string | null;
  location_naam: string | null;
  department_naam: string | null;
}

export type TeamStatus = 'actief' | 'uitgenodigd' | 'ingetrokken';

export const teamStatus = (m: TeamMember): TeamStatus =>
  !m.is_active ? 'ingetrokken' : m.accepted_at ? 'actief' : 'uitgenodigd';

export const STATUS_KLASSEN: Record<TeamStatus, string> = {
  actief: 'bg-green-100 text-green-800',
  uitgenodigd: 'bg-amber-100 text-amber-800',
  ingetrokken: 'bg-gray-200 text-gray-600',
};

const AVATAR_KLASSEN: Record<TeamStatus, string> = {
  actief: 'bg-blue-600 text-white',
  uitgenodigd: 'bg-amber-100 text-amber-800 ring-1 ring-dashed ring-amber-400',
  ingetrokken: 'bg-gray-200 text-gray-500 line-through',
};

export const rolLabel = (role: string) => EMPLOYER_ROLE_PRESETS.find((p) => p.role === role)?.label || role;

const initialen = (voornaam: string, achternaam: string) =>
  `${voornaam?.[0] || ''}${achternaam?.[0] || ''}`.toUpperCase() || '?';

/** Plek in de boom: vestiging en afdeling (NULL = concern-breed / alle afdelingen). */
interface Plek {
  location_id: string | null;
  department_id: string | null;
}

interface ToegangsBoomProps {
  members: TeamMember[];
  locations: EmployerLocation[];
  departments: EmployerDepartment[];
  organisatieNaam: string;
  /** null = concerntop verborgen (Vestigingsmanager) */
  hoofdgebruikerNaam: string | null;
  /** Mag iemand naar de wortel (concern-breed) worden gesleept */
  wortelBewerkbaar: boolean;
  onEdit: (member: TeamMember) => void;
  onMove: (member: TeamMember, plek: Plek) => void;
}

/**
 * Organisatieboom van Toegangsbeheer & Rechten: organisatie → vestigingen →
 * afdelingen, met per knooppunt de collega's die daar hun toegang hebben.
 * Een persoon verslepen naar een ander knooppunt verplaatst de Toegangs-Scope;
 * klikken opent het wijzigvenster.
 */
export const ToegangsBoom: React.FC<ToegangsBoomProps> = ({
  members,
  locations,
  departments,
  organisatieNaam,
  hoofdgebruikerNaam,
  wortelBewerkbaar,
  onEdit,
  onMove,
}) => {
  const { t } = useTranslation('employer');
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [sleepId, setSleepId] = useState<string | null>(null);
  const [dropDoel, setDropDoel] = useState<string | null>(null);

  const ledenOp = (locationId: string | null, departmentId: string | null) =>
    members.filter((m) => m.location_id === locationId && m.department_id === departmentId);

  const universeleAfdelingen = departments.filter((d) => !d.location_id);
  const concernBredeAfdelingen = universeleAfdelingen.filter((d) => ledenOp(null, d.id).length > 0);

  const sleepLid = members.find((m) => m.user_id === sleepId) || null;

  const dropProps = (sleutel: string, plek: Plek) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!sleepLid) return;
      e.preventDefault();
      if (dropDoel !== sleutel) setDropDoel(sleutel);
    },
    onDragLeave: () => setDropDoel((d) => (d === sleutel ? null : d)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropDoel(null);
      setSleepId(null);
      if (!sleepLid) return;
      if (sleepLid.location_id === plek.location_id && sleepLid.department_id === plek.department_id) return;
      onMove(sleepLid, plek);
    },
  });

  const dropKlasse = (sleutel: string) =>
    dropDoel === sleutel ? 'ring-2 ring-blue-500 bg-blue-50' : sleepLid ? 'ring-1 ring-dashed ring-blue-300' : '';

  const bevatHover = (leden: TeamMember[]) => !!hoverId && leden.some((m) => m.user_id === hoverId);

  const persoon = (m: TeamMember) => {
    const status = teamStatus(m);
    const actief = hoverId === m.user_id;
    return (
      <div
        key={m.user_id}
        className="relative"
        onMouseEnter={() => setHoverId(m.user_id)}
        onMouseLeave={() => setHoverId((h) => (h === m.user_id ? null : h))}
      >
        <button
          type="button"
          draggable={m.bewerkbaar}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', m.user_id);
            setSleepId(m.user_id);
            setHoverId(null);
          }}
          onDragEnd={() => {
            setSleepId(null);
            setDropDoel(null);
          }}
          onClick={() => m.bewerkbaar && onEdit(m)}
          aria-disabled={!m.bewerkbaar}
          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
            m.bewerkbaar ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
          } ${
            actief ? 'bg-blue-50' : 'hover:bg-gray-50'
          } ${sleepId === m.user_id ? 'opacity-40' : ''}`}
          aria-label={t('team.boomWijzig', { name: `${m.first_name} ${m.last_name}` })}
        >
          <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${AVATAR_KLASSEN[status]}`}>
            {initialen(m.first_name, m.last_name)}
          </span>
          <span className="min-w-0">
            <span className={`block text-sm font-medium truncate ${status === 'ingetrokken' ? 'text-gray-400' : 'text-gray-900'}`}>
              {m.first_name} {m.last_name}
            </span>
            <span className="block text-xs text-gray-500 truncate">
              {m.functietitel || rolLabel(m.team_role)}
            </span>
          </span>
        </button>
        {actief && !sleepId && (
          <div className="absolute z-20 left-0 top-full mt-1 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-xs pointer-events-none">
            <p className="text-sm font-semibold text-gray-900">{m.first_name} {m.last_name}</p>
            <p className="text-gray-500 break-all">{m.email}</p>
            <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
              <dt className="text-gray-500">{t('team.colFunctie')}</dt>
              <dd className="text-gray-900">{m.functietitel || '—'}</dd>
              <dt className="text-gray-500">{t('team.colNiveau')}</dt>
              <dd className="text-gray-900">{t(`team.niveau_${m.toegangsniveau}`)}</dd>
              <dt className="text-gray-500">{t('team.colRole')}</dt>
              <dd className="text-gray-900">{rolLabel(m.team_role)}</dd>
              <dt className="text-gray-500">{t('team.colStatus')}</dt>
              <dd>
                <span className={`inline-flex px-1.5 py-0.5 rounded-full font-medium ${STATUS_KLASSEN[status]}`}>
                  {t(`team.status_${status}`)}
                </span>
              </dd>
            </dl>
            {!m.bewerkbaar && <p className="mt-2 text-gray-400">{t('team.nietBewerkbaar')}</p>}
          </div>
        )}
      </div>
    );
  };

  const personen = (leden: TeamMember[]) =>
    leden.length === 0 ? (
      <p className="px-2 py-1 text-xs text-gray-400 italic">{t('team.boomLeeg')}</p>
    ) : (
      <div className="space-y-0.5">
        {leden.map(persoon)}
      </div>
    );

  /** Afdelingsknooppunt met aftakking vanaf de verticale lijn van de vestiging. */
  const afdelingNode = (sleutel: string, naam: string, universeel: boolean, leden: TeamMember[], plek: Plek) => {
    const licht = bevatHover(leden);
    return (
      <div key={sleutel} className="relative pl-5">
        <span className={`absolute left-0 top-5 w-5 border-t-2 transition-colors ${licht ? 'border-blue-500' : 'border-gray-200'}`} />
        <div
          {...dropProps(sleutel, plek)}
          className={`rounded-lg border bg-white p-2 transition-all ${licht ? 'border-blue-400 shadow-sm' : 'border-gray-200'} ${dropKlasse(sleutel)}`}
        >
          <div className="flex items-center gap-1.5 px-1 pb-1 mb-1 border-b border-gray-100">
            <Layers className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700 truncate">{naam}</span>
            {universeel && <span className="ml-auto text-[10px] text-gray-400">{t('team.boomUniverseel')}</span>}
          </div>
          {personen(leden)}
        </div>
      </div>
    );
  };

  /** concern = kolom "Alle vestigingen": alleen universele afdelingen, de directe leden staan al in de wortel. */
  const vestigingKolom = (sleutel: string, titel: string, subtitel: string, plek: Plek, afdelingen: EmployerDepartment[], concern = false) => {
    const directeLeden = concern ? [] : ledenOp(plek.location_id, null);
    const licht = bevatHover(members.filter((m) => m.location_id === plek.location_id && (plek.location_id || m.department_id)));
    return (
      <div key={sleutel} className="relative w-64 flex-shrink-0 pt-5">
        <span className={`absolute left-1/2 top-0 h-5 border-l-2 transition-colors ${licht ? 'border-blue-500' : 'border-gray-200'}`} />
        <div
          {...(concern ? {} : dropProps(sleutel, plek))}
          className={`rounded-xl border-2 bg-white p-3 transition-all ${licht ? 'border-blue-400' : 'border-gray-200'} ${concern ? '' : dropKlasse(sleutel)}`}
        >
          <div className="flex items-start gap-2 mb-2">
            <MapPin className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{titel}</p>
              {subtitel && <p className="text-xs text-gray-500 truncate">{subtitel}</p>}
            </div>
          </div>
          {!concern && (directeLeden.length > 0 || afdelingen.length === 0) && personen(directeLeden)}
        </div>
        {afdelingen.length > 0 && (
          <div className="relative ml-4 mt-0 pt-3 space-y-3">
            <span className="absolute left-0 top-0 bottom-5 border-l-2 border-gray-200" />
            {afdelingen.map((d) =>
              afdelingNode(`${sleutel}:${d.id}`, d.naam, !d.location_id, ledenOp(plek.location_id, d.id), {
                location_id: plek.location_id,
                department_id: d.id,
              })
            )}
          </div>
        )}
      </div>
    );
  };

  const rootLeden = ledenOp(null, null);
  const toonConcernKolom = concernBredeAfdelingen.length > 0;

  return (
    <div className="overflow-x-auto pb-4">
      <div className="inline-flex min-w-full flex-col items-center">
        {/* Organisatie (wortel) */}
        <div
          {...(wortelBewerkbaar ? dropProps('root', { location_id: null, department_id: null }) : {})}
          className={`w-80 rounded-xl border-2 border-blue-200 bg-gradient-to-b from-blue-50 to-white p-3 transition-all ${wortelBewerkbaar ? dropKlasse('root') : ''}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-blue-700" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">{organisatieNaam}</p>
              <p className="text-xs text-gray-500">{t('team.boomConcern')}</p>
            </div>
          </div>
          {hoofdgebruikerNaam && (
          <div className="flex items-center gap-2 rounded-lg px-2 py-1.5 bg-white/70">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center">
              <Crown className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 truncate">{hoofdgebruikerNaam}</span>
              <span className="block text-xs text-gray-500">{t('team.boomHoofdgebruiker')}</span>
            </span>
          </div>
          )}
          {rootLeden.length > 0 && <div className="mt-1">{personen(rootLeden)}</div>}
        </div>

        {(locations.length > 0 || toonConcernKolom) && (
          <>
            <span className="h-5 border-l-2 border-gray-200" />
            <div className="relative flex gap-6 px-4">
              {/* Horizontale verbindingslijn tussen de vestigingen */}
              <span className="absolute top-0 left-[calc(1rem+8rem)] right-[calc(1rem+8rem)] border-t-2 border-gray-200" />
              {toonConcernKolom &&
                vestigingKolom(
                  'concern',
                  t('team.scopeAlleVestigingen'),
                  t('team.boomConcernAfdelingen'),
                  { location_id: null, department_id: null },
                  concernBredeAfdelingen,
                  true
                )}
              {locations.map((loc) => {
                const exclusief = departments.filter((d) => d.location_id === loc.id);
                const universeelMetLeden = universeleAfdelingen.filter((d) => ledenOp(loc.id, d.id).length > 0);
                return vestigingKolom(
                  `loc:${loc.id}`,
                  loc.naam,
                  [loc.plaats, loc.is_hoofdvestiging ? t('team.boomHoofdvestiging') : null].filter(Boolean).join(' · '),
                  { location_id: loc.id, department_id: null },
                  [...exclusief, ...universeelMetLeden]
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
